import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useApiQuery, useToast } from 'grabbitbot-legacy-ui'
import { wnfClient } from '../../api'
import type { CoinTransaction, SwapQuote } from '../../api/types'
import { HelpTip } from '../../components/HelpTip'
import { PageState } from '../../components/PageState'
import { TestFundsBanner } from '../../components/TestFundsBanner'
import { describeLoadError, describeSwapError, isNoPairError } from '../../lib/errors'
import { decimalIsPositive, money } from '../../lib/format'
import { useDepositableCoins } from '../../lib/useDepositableCoins'

const QUOTE_DEBOUNCE_MS = 400

type QuoteState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; quote: SwapQuote }
  | { kind: 'no_pair' }
  | { kind: 'error'; message: string }

/** What the user actually received, read off the fill (works for either side). */
function receivedOf(fill: CoinTransaction, to: string): string {
  return to === fill.coin1 ? fill.amount1 : fill.amount2
}

/** Manual coin conversion: pick from / to / amount, see a live quote, swap.
 * The swap is executed by the backend as a market order. */
export function SwapPage() {
  const { showToast } = useToast()
  const wallets = useApiQuery(() => wnfClient.searchWallets(), [])
  const depositable = useDepositableCoins()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [amount, setAmount] = useState('')
  const [quoteState, setQuoteState] = useState<QuoteState>({ kind: 'idle' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [last, setLast] = useState<{ from: string; to: string; spent: string; received: string } | null>(null)

  // Wallets the user can swap OUT of: free balance above zero.
  const held = useMemo(
    () =>
      (wallets.data ?? [])
        .filter((w) => decimalIsPositive(String(w.balance)))
        .sort((a, b) => a.coin.localeCompare(b.coin)),
    [wallets.data],
  )
  const fromCoin = from && held.some((w) => w.coin === from) ? from : (held[0]?.coin ?? '')
  const fromBalance = held.find((w) => w.coin === fromCoin)?.balance
  // Swap INTO: every depositable coin plus anything the user already holds.
  const toOptions = useMemo(() => {
    const all = new Set<string>([...depositable.coins.map((c) => c.coin), ...(wallets.data ?? []).map((w) => w.coin)])
    all.delete(fromCoin)
    return [...all].sort()
  }, [depositable.coins, wallets.data, fromCoin])
  const toCoin = to && toOptions.includes(to) ? to : ''

  const trimmed = amount.trim()
  const amountOk = decimalIsPositive(trimmed)

  // Debounced live quote; stale answers are dropped.
  useEffect(() => {
    if (!fromCoin || !toCoin || !amountOk) {
      setQuoteState({ kind: 'idle' })
      return
    }
    let cancelled = false
    setQuoteState({ kind: 'loading' })
    const timer = setTimeout(() => {
      wnfClient
        .quoteSwap(fromCoin, toCoin, trimmed)
        .then((quote) => {
          if (!cancelled) setQuoteState({ kind: 'ok', quote })
        })
        .catch((err: unknown) => {
          if (cancelled) return
          setQuoteState(isNoPairError(err) ? { kind: 'no_pair' } : { kind: 'error', message: describeSwapError(err) })
        })
    }, QUOTE_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [fromCoin, toCoin, trimmed, amountOk])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (!fromCoin || !toCoin) {
      setError('Choose the coin to swap from and the coin to swap into.')
      return
    }
    if (!amountOk) {
      setError('Please enter an amount greater than zero.')
      return
    }
    setBusy(true)
    try {
      const fill = await wnfClient.swap(fromCoin, toCoin, trimmed, `ui-swap-${Date.now()}`)
      const received = receivedOf(fill, toCoin)
      setLast({ from: fromCoin, to: toCoin, spent: trimmed, received })
      showToast(`Swapped ${trimmed} ${fromCoin} for ${received} ${toCoin}`, 'success')
      setAmount('')
      setQuoteState({ kind: 'idle' })
      wallets.refetch()
    } catch (err) {
      setError(describeSwapError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="wnf-page-title">
        <h1>Swap — spot</h1>
      </div>
      <TestFundsBanner />
      <PageState loading={wallets.loading} error={wallets.error ? describeLoadError(wallets.error, 'wallets') : null}>
        {held.length === 0 ? (
          <p className="wnf-muted" data-testid="swap-no-funds">
            You hold no funds to swap yet — deposit test funds first.
          </p>
        ) : (
          <div className="wnf-swap">
            <form className="wnf-card wnf-form" onSubmit={submit} aria-label="Swap coins" data-testid="swap-form">
              <h2>Convert coins</h2>
              <label>
                From
                <select value={fromCoin} onChange={(e) => setFrom(e.target.value)} aria-label="From coin">
                  {held.map((w) => (
                    <option key={w.coin} value={w.coin}>
                      {w.coin} — free {money(w.balance)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                To
                <select value={toCoin} onChange={(e) => setTo(e.target.value)} aria-label="To coin">
                  <option value="">Select a coin…</option>
                  {toOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Amount ({fromCoin})
                <span className="wnf-swap__amount">
                  <input
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.0"
                    aria-label="Swap amount"
                  />
                  <button type="button" onClick={() => fromBalance !== undefined && setAmount(String(fromBalance))}>
                    Max
                  </button>
                </span>
              </label>
              {error && <p role="alert">{error}</p>}
              <button type="submit" disabled={busy}>
                {busy ? 'Working…' : 'Swap'}
              </button>
            </form>

            <section className="wnf-card wnf-quote" aria-label="Quote" data-testid="swap-quote" aria-live="polite">
              <h2>Quote</h2>
              {quoteState.kind === 'idle' && <p className="wnf-muted">Choose both coins and an amount to see a live quote.</p>}
              {quoteState.kind === 'loading' && <p aria-busy="true">Getting quote…</p>}
              {quoteState.kind === 'no_pair' && (
                <p role="alert" data-testid="swap-no-pair">
                  No trading pair between {fromCoin} and {toCoin} — they cannot be swapped directly. Try swapping through USDT or USDC.
                </p>
              )}
              {quoteState.kind === 'error' && <p role="alert">{quoteState.message}</p>}
              {quoteState.kind === 'ok' && (
                <dl className="wnf-quote__list">
                  <div className="wnf-quote__row">
                    <dt>Pair</dt>
                    <dd>{quoteState.quote.coinPair}</dd>
                  </div>
                  <div className="wnf-quote__row">
                    <dt>Side</dt>
                    <dd>
                      <span className={`wnf-badge wnf-badge--${quoteState.quote.side === 'sell' ? 'sell' : 'buy'}`}>
                        {quoteState.quote.side}
                      </span>
                    </dd>
                  </div>
                  <div className="wnf-quote__row">
                    <dt>
                      <HelpTip label="Price">
                        The market price of the pair that this swap would be filled at. There is no good or bad range: compare it with the
                        price you saw earlier, or with the same pair on a real exchange.
                      </HelpTip>
                    </dt>
                    <dd className="wnf-num" data-testid="quote-price">{money(quoteState.quote.price)}</dd>
                  </div>
                  <div className="wnf-quote__row">
                    <dt>
                      <HelpTip label="Estimated receive">
                        About how much of the “To” coin you would get for this amount. A higher number is better for you, but it is only an
                        estimate: the swap is filled as a market order, so the final amount can differ if the price moves. There is no
                        benchmark range; compare it with the amount multiplied by the price.
                      </HelpTip>
                    </dt>
                    <dd className="wnf-num wnf-quote__receive" data-testid="quote-receive">
                      {money(quoteState.quote.estimatedReceive)} {toCoin}
                    </dd>
                  </div>
                </dl>
              )}
              {last && (
                <p className="wnf-muted" data-testid="swap-last">
                  Last swap: {last.spent} {last.from} → {last.received} {last.to}
                </p>
              )}
            </section>
          </div>
        )}
      </PageState>
    </div>
  )
}
